import React, { useState, useEffect } from 'react';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { Button } from '@uhg-abyss/web/ui/Button';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { userAgent } from '@uhg-abyss/web/tools/userAgent';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { styled } from '@uhg-abyss/web/tools/styled';
import { storage } from '@uhg-abyss/web/tools/storage';
import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { usePPDLagoon } from '../../../../tools/lagoon';

const Wrapper = styled('div', { height: 32 });

const StyledTextInput = styled(TextInput, {
  borderRadius: 24,
  '& input': {
    borderRadius: 24,
  },
  '& .abyss-text-input, & .abyss-form-input': {
    borderRadius: 24,
  },
  '& .abyss-form-input-wrapper-container': {
    borderRadius: 24,
  },
  '& .abyss-text-input-root': {
    borderRadius: 24,
  },
  '& .abyss-form-input-wrapper-container input': {
    borderRadius: 24,
  },
});

const StyledTitle = styled('div', {
  fontWeight: 'bold',
  fontSize: '1rem',
  marginBottom: '.5rem',
  padding: [5, 0, 10, 0],
  borderBottom: '2px solid $gray4',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
});

const OuterWrapper = styled('div', {
  display: 'inline-block',
});

const DropdownWrapper = styled('div', {
  position: 'absolute',
  minWidth: 300,
  backgroundColor: '$primary2',
  border: '1px solid $gray4',
  borderRadius: 6,
  zIndex: 1000,
  padding: 16,
});

const StyledResults = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  textAlign: 'left',
  justifyContent: 'space-between',
  alignItems: 'space-between',
  borderBottom: '1px solid $gray4',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const DropDownContent = styled('div', {
  height: userAgent.isIE ? 'auto' : 200,
  right: 0,
  left: 56,
  border: 'none',
  borderRadius: 6,
  overflowY: userAgent.isIE ? 'auto' : 'scroll',
});

const baseButtonStyles = {
  '&.abyss-button-root': {
    fontSize: '1rem',
    lineHeight: 1,
    textAlign: 'left',
    zIndex: 2000,
    color: '#196ECF',
  },
};

const StyledClearButton = styled(Button, {
  ...baseButtonStyles,
  '&.abyss-button-root': {
    ...baseButtonStyles['&.abyss-button-root'],
    display: 'flex',
    alignItems: 'flex-end !important',
    paddingBottom: '3px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
});

const StyledSearchedItemsButton = styled(Button, {
  ...baseButtonStyles,
  '&.abyss-button-root': {
    ...baseButtonStyles['&.abyss-button-root'],
    paddingLeft: '0px !important',
    paddingRight: '0px !important',
  },
});

const StyledTrashIconButton = styled(Button, {
  ...baseButtonStyles,
  '&.abyss-button-root': {
    ...baseButtonStyles['&.abyss-button-root'],
    paddingLeft: '10px',
    paddingRight: '10px',
  },
});

const StyledSpan = styled('span', {
  float: 'right',
  marginRight: '4px',
});

export const AppSearchSelect = () => {
  const { onItemSelect } = usePropsContext();
  const form = useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [, setAppSearchData] = useState();
  const [recentSearches, setRecentSearches] = useState(
    storage.local.get('app-search') || []
  );
  const [displayResults, setDisplayResults] = useState([]);

  const { getHeaderContentsForSearch, getAdditionalToolsForSearch } =
    usePPDLagoon();

  const addValueToStorage = (value) => {
    const existingValues = storage.local.get('app-search');
    const newValues = existingValues || [];
    newValues.unshift(value);
    const filteredValues = uniqBy(newValues, 'name').slice(0, 5);
    storage.local.set('app-search', filteredValues);
    setRecentSearches(filteredValues);
  };

  const handleClearRecent = () => {
    storage.local.clear('app-search');
    setRecentSearches([]);
  };

  const handleItemSelect = (item) => {
    form.setValue('appsearch.name', item.name);
    onItemSelect(item.name);
    setIsOpen(false);
    form.reset();
  };

  const handleSubmit = () => {
    const formValue = form.getValues('appsearch');
    const regex = /<[^>]+>/g; // Regular expression to match HTML tags
    if (formValue.name) {
      if (formValue.name.includes('%') || regex.test(formValue.name)) {
        return;
      }
      addValueToStorage(formValue);
      setIsOpen(false);
      onItemSelect(formValue.name);
      form.reset();
    }
  };

  useEffect(() => {
    const headerData = getHeaderContentsForSearch();
    const additionalToolsData = getAdditionalToolsForSearch();

    const combinedSearchData = uniqBy(
      sortBy(
        (headerData || '').concat(additionalToolsData),
        (result) => result.name
      ),
      'name'
    );

    setAppSearchData(combinedSearchData);
  }, []);

  const handleOnFocus = (e) => {
    setIsOpen(true);
  };

  const handleOnBlur = (e) => {
    const relatedTarget =
      e.relatedTarget || e.explicitOriginalTarget || document.activeElement;

    if (!e.currentTarget.contains(relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setDisplayResults([]);
  };

  const handleRemoveRecent = (index) => {
    const recentSearchesCopy = [...recentSearches];
    recentSearchesCopy.splice(index, 1);
    setRecentSearches(recentSearchesCopy);
    storage.local.set('app-search', recentSearchesCopy);
  };

  return (
    <FormProvider state={form}>
      <Wrapper
        role='button'
        tabIndex={0}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
      >
        <StyledTextInput
          model='appsearch.name'
          onClear={handleClear}
          placeholder='Search'
          maxLength={50}
          data-testid='search-component-input'
          isClearable
          type='search'
          onClickSearch={handleSubmit}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit}
          label='Search'
          hideLabel
        />
        <If
          condition={
            isOpen && (displayResults.length > 0 || recentSearches.length > 0)
          }
        >
          <OuterWrapper>
            <DropdownWrapper>
              <DropDownContent>
                <Choose>
                  <When condition={recentSearches.length > 0}>
                    <StyledTitle data-testid='recent-searches-label'>
                      Recent Searches
                      <StyledSpan>
                        <StyledClearButton
                          onClick={handleClearRecent}
                          variant='native'
                          data-testid='clear-buton'
                        >
                          Clear
                        </StyledClearButton>
                      </StyledSpan>
                    </StyledTitle>
                    <For each='result' of={recentSearches} index='index'>
                      <StyledResults key={result.id}>
                        <StyledSearchedItemsButton
                          variant='native'
                          size='size400'
                          data-testid='recet-search-links'
                          to={`/app-search/${result.name}`}
                          onClick={() => handleItemSelect(result)}
                        >
                          {result.name}
                        </StyledSearchedItemsButton>
                        <StyledSpan>
                          <StyledTrashIconButton
                            variant='native'
                            data-testid='trash-icon'
                          >
                            <IconMaterial
                              icon='delete'
                              size={14}
                              onClick={() => handleRemoveRecent(index)}
                            />
                          </StyledTrashIconButton>
                        </StyledSpan>
                      </StyledResults>
                    </For>
                  </When>
                </Choose>
              </DropDownContent>
            </DropdownWrapper>
          </OuterWrapper>
        </If>
      </Wrapper>
    </FormProvider>
  );
};
